<template>
    <div class="field-search">
        <div class="field-search__top" :class="{ 'field-search__top--single' : simpleSearch }">
            <div class="form__field-wrapper">
                <i class="form__field-icon icon icon--common-search field-search__search" :class="{'icon--common-x field-search__search--clear' : this.searchInput !== ''}" @click="clearSearchHandler"></i>
                <input
                    class="form__field field-search__input"
                    type="text"
                    :placeholder="placeholder"
                    @input="searchInputHandler"
                    v-model="searchInput"
                />
            </div>
            <div v-if="!simpleSearch" class="field-search__toggle" :class="{'closed' : !dropdownVisible}" @click="toggleDropdownHandler">
                <i class="icon icon--common-caret"></i>
            </div>
        </div>
        <transition name="fade-down" v-if="!simpleSearch">
            <div class="field-search__dropdown" v-if="dropdownVisible">
                <div class="field-search__field" v-for="(field, index) in fields" :key="index">
                    <div class="field-search__field__left">
                        <select class="form__field" v-model="field.name" @change="fieldInputHandler()">
                            <option
                                v-for="allowedField in translatedAllowedFields"
                                :key="allowedField.value"
                                :value="allowedField.value"
                                :disabled="!selectableFields.includes(allowedField.value)"
                            >
                                {{ allowedField.label }}</option
                            >
                        </select>
                    </div>
                    <div class="field-search__field__right">
                        <input class="form__field" type="text" v-model="field.value" @input="fieldInputHandler()"></input>
                        <div v-if="fields.length > 1" class="field-search__field__remove" @click="removeFieldHandler(index)">
                            <i class="icon icon--common-x"></i>
                        </div>
                    </div>
                </div>
                <div v-if="selectableFields.length > 0" class="field-search__add button button--primary" @click="addFieldHandler">
                    +
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { getSearchParamMap } from '@/utils/searchParameters'
import eventBus from '@/utils/eventBus';

export default {
    props: {
        placeholder: {
            type: String,
            default: '',
        },
        allowedFields: {
            type: Array,
            default: () => ['name'],
        },
        defaultField: {
            type: String,
            default: 'name',
        },
        nameTranslation: {
            type: String,
            default: 'name',
        },
        savedSearchName: {
            type: [String, Boolean],
            default: false,
        },
        simpleSearch: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            searchInput: '',
            dropdownVisible: false,
            fields: [
                {
                    name: 'name',
                    value: '',
                },
            ],
            fieldTranslations: {
                name: 'users_admin.name',
                email: 'fields.email',
                country: 'fields.country',
                postcode: 'fields.postcode',
                city: 'fields.city',
                title: 'fields.title',
                body: 'fields.body',
                language: 'notifications.language',
                triggered_by: 'notifications.triggered_by_name',
                triggered_at: 'notifications.sent_on',
                date: 'certificates.created_at',
                mac: 'fields.mac_address',
                sn: 'fields.serial_number',
                owner: 'systems.permission.owner',
                resident: 'systems.permission.resident',
                technician: 'systems.permission.technician',
                operator: 'systems.permission.operator',
                addr: 'overview.address',
                address: 'overview.address',
                label: 'fields.labels',
                errorcode: 'systems.logs.message',
                system: 'systems.system',
                component: 'fields.component',
                hw: 'fields.hardware',
                sw: 'fields.software',
                hours: 'fields.hours',
                starts: 'fields.starts',
                consumption: 'fields.consumption',
                outputRating: 'fields.outputRating',
                parameter: 'systems.parameter_logs.parameter',
                oid: 'OID',
                user_name: 'systems.parameter_logs.user',
                node_name: 'systems.logs.source_of_error',
                error_code: 'systems.logs.number',
                error_message: 'systems.logs.message',
                predictivecode: 'fields.maintenance',
            },
        };
    },
    computed: {
        selectableFields() {
            const selectableFields = []
            this.allowedFields.forEach(allowedField => {
                const existingFields = [...this.fields]
                const existingField = existingFields.find(field => { return field.name === allowedField })
                if(!existingField) {
                    selectableFields.push(allowedField)
                }
            })
            return selectableFields
        },
        translatedAllowedFields() {
            return this.allowedFields.map(field => {
                const translationKey = field === 'name' ? this.nameTranslation : field
                const label = this.fieldTranslations[translationKey] ? this.$t(this.fieldTranslations[translationKey]) : field

                return {
                    value: field,
                    label: label,
                }
            }) 
        },
    },
    watch: {
        searchInput() {
            if(!this.simpleSearch) this.saveSearchValueToStorage()
        },
    },
    mounted() {
        this.fields[0].name = this.defaultField
        if(this.simpleSearch){
            this.searchInput = ''
        } else {
            this.checkSavedSearchValues()
        }
        this.addListeners()
    },
    beforeDestroy() {
        this.removeListeners()
    },
    methods: {
        clearSearchHandler() {
            this.searchInput = ''
            this.fields = [
                {
                    name: this.defaultField,
                    value: '',
                },
            ],
            this.fieldInputHandler()
        },
        toggleDropdownHandler() {
            this.dropdownVisible = !this.dropdownVisible
        },
        addFieldHandler() {
            this.fields.push({
                name: this.selectableFields[0],
                value: '',
            })
        },
        fieldInputHandler() {
            let inputString = ''

            this.fields.forEach(field => {
                if(field.value !== '') {
                    let fieldString = `${field.name}: ${field.value};`
                    if(field.value.indexOf(' ') >= 0) {
                        fieldString = `${field.name}: "${field.value}";`
                    }
                    inputString += fieldString
                }
            })

            this.searchInput = inputString
            this.$emit('search', this.searchInput)
        },
        removeFieldHandler(index) {
            this.fields.splice(index, 1)
            this.fieldInputHandler()
        },
        searchInputHandler() {
            this.getFieldsFromInput()
            this.$emit('search', this.searchInput)
        },
        addListeners() {
            eventBus.$on('clearSearch', this.clearSearchHandler);
        },
        removeListeners() {
            eventBus.$off('clearSearch', this.clearSearchHandler);
        },
        getFieldsFromInput() {
            const map = getSearchParamMap(this.searchInput, this.allowedFields, this.defaultField)
            this.fields = map
        },
        setInitialSearchValue(value) {
            const storageItem = window.localStorage.getItem(this.savedSearchName)
            if(storageItem && storageItem !== '') return
            this.searchInput = value
            this.$emit('search', this.searchInput)
        },
        clear() {
            this.clearSearchHandler()
        },
        saveSearchValueToStorage() {
            if(this.savedSearchName && this.savedSearchName !== '') {
                window.localStorage.setItem(this.savedSearchName, this.searchInput)
            }
        },
        checkSavedSearchValues() {
            if(!this.savedSearchName || this.savedSearchName === '') return
            const storageItem = window.localStorage.getItem(this.savedSearchName)
            if(storageItem && storageItem !== '') {
                this.searchInput = window.localStorage.getItem(this.savedSearchName)
                this.getFieldsFromInput()
                this.$emit('search', this.searchInput)
            }
        },
    },
};
</script>

<style lang="scss">
.field-search {
    $block: &;
    position: relative;
    z-index: 2;

    &__top {
        display: grid;
        grid-template-columns: auto 30px;

        &--single {
            grid-template-columns: auto;
        }
    }

    &__dropdown {
        position: absolute;
        text-align: left;
        top: 100%;
        left: 0;
        width: 100%;
        padding: 10px;
        background: $color-gray-light;
        box-shadow: 0px 3px 11px -6px rgba(0,0,0,0.75);
        display: flex;
        flex-direction: column;
    }

    &__field {
        display: grid;
        grid-template-columns: 1fr 1.5fr;
        gap: 10px;

        &:not(:last-child) {
            margin-bottom: 10px;
        }

        @media (hover: hover) {
            &:hover {
                #{$block}__field {
                    &__remove {
                        opacity: 1;
                        pointer-events: all;
                    }
                }
            }
        }

        &__right {
            position: relative;
        }

        &__remove {
            position: absolute;
            top: 50%;
            right: 10px;
            transform: translateY(-50%);
            font-size: 10px;
            opacity: 0;
            pointer-events: none;
            transition: 300ms ease;
            cursor: pointer;
        }
    }

    &__search {
        &--clear {
            font-size: 10px;
            cursor: pointer;
        }
    }

    &__toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 7px;
        right: 12px;
        background: $color-gray-mid;
        cursor: pointer;

        &.closed {
            .icon {
                transform: rotate(180deg);
            }
        }

        .icon {
            transition: all 300ms ease;
        }
    }

    &__add {
        width: 24px;
        padding: 0;
        height: 24px;
        margin-left: auto;
        line-height: 23px;
        text-align: center;
    }

    &__input {
        padding-right: 46px;
        text-overflow: ellipsis;
    }
}
</style>
